import { startCase } from "lodash";
import { FC, useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  FramerTooltip,
  GroupTitleTemplate,
  SwitchTemplate,
  TemplateWrapper,
  TextareaTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import {
  IBulkDriversMessageProps,
  IInjectedDrawerProps,
  onActivateKeyDown,
  PLURAL_KEY,
  SHOWN_PASSENGER_NAMES_LENGTH,
} from "@ryde/shared";

import { LayoutRightLoader } from "../../../right";
import {
  BulkPassengersMessageCountStyled,
  BulkPassengersMessagePassengersListStyled,
  PassengersMessageAvailableTemplateStyled,
} from "../bulk-passengers-message/parts";
import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, DrawerFormWrapperStyled, FormFooter } from "../drawer";
import {
  BulkDriversMessageFormFieldsEnum,
  driversMessageKeysMappedTemplates,
  getDriversNames,
  getPreviewMessage,
  useBulkDriversMessageDrawer,
} from "./parts";

export const BulkDriversMessageDrawer: FC<
  IBulkDriversMessageProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber, selectedDriverIds }) => {
  const { t } = useTranslation();
  const { methods, isSaveDisabled, drivers, isLoading, onSaveClick } =
    useBulkDriversMessageDrawer({ selectedDriverIds });

  const { watch, setValue } = methods;

  const isSendViaEmailValueWatch = watch(
    BulkDriversMessageFormFieldsEnum.isEmail,
  );
  const messageValueWatch = watch(BulkDriversMessageFormFieldsEnum.message);

  const driverNames = useMemo(() => getDriversNames(drivers), [drivers]);

  const previewMessageComputed = useMemo(
    () => getPreviewMessage(messageValueWatch, drivers),
    [messageValueWatch, drivers],
  );

  const handleSaveClick = useEvent(() => {
    onSaveClick();
  });

  useEffect(() => {
    setValue(BulkDriversMessageFormFieldsEnum.preview, previewMessageComputed, {
      shouldTouch: true,
      shouldDirty: false,
    });
  }, [previewMessageComputed, setValue]);

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("driver.SEND_MESSAGES_TO_DRIVERS")}
        onCloseDrawer={onCloseDrawer}
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={onCloseDrawer}
            onSaveClick={handleSaveClick}
            isSaveDisabled={isSaveDisabled}
          />
        }
      >
        <FormProvider {...methods}>
          {!isLoading && (
            <DrawerFormWrapperStyled>
              <BulkPassengersMessageCountStyled>
                <span>{startCase(t(`driver.${PLURAL_KEY}`))}</span>
                <span>{drivers?.length}</span>
              </BulkPassengersMessageCountStyled>
              <BulkPassengersMessagePassengersListStyled>
                <span>{driverNames}</span>
                <span>
                  {drivers.length > SHOWN_PASSENGER_NAMES_LENGTH
                    ? `+${drivers.length - SHOWN_PASSENGER_NAMES_LENGTH}`
                    : ""}
                </span>
              </BulkPassengersMessagePassengersListStyled>

              <TemplateWrapper>
                <GroupTitleTemplate
                  name="details_title"
                  labelTranslated={t("driver.sendDriverMessageForm.details")}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <SwitchTemplate
                  name={BulkDriversMessageFormFieldsEnum.isEmail}
                  labelTranslated={t(
                    "driver.sendDriverMessageForm.sendViaEmail",
                  )}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <SwitchTemplate
                  name={BulkDriversMessageFormFieldsEnum.isSms}
                  labelTranslated={t("driver.sendDriverMessageForm.sendViaSms")}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <SwitchTemplate
                  name={BulkDriversMessageFormFieldsEnum.isPush}
                  labelTranslated={t(
                    "driver.sendDriverMessageForm.sendViaPush",
                  )}
                />
              </TemplateWrapper>

              {isSendViaEmailValueWatch && (
                <TemplateWrapper>
                  <TextboxTemplate
                    name={BulkDriversMessageFormFieldsEnum.subject}
                    labelTranslated={t("driver.sendDriverMessageForm.subject")}
                  />
                </TemplateWrapper>
              )}

              <TemplateWrapper>
                <TextareaTemplate
                  name={BulkDriversMessageFormFieldsEnum.preview}
                  labelTranslated={t("driver.sendDriverMessageForm.preview")}
                  formAttributes={{ disabled: true }}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <TextareaTemplate
                  name={BulkDriversMessageFormFieldsEnum.message}
                  labelTranslated={t("driver.sendDriverMessageForm.message")}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <PassengersMessageAvailableTemplateStyled>
                  {Object.entries(driversMessageKeysMappedTemplates).map(
                    ([key, translationPath]) =>
                      translationPath ? (
                        <FramerTooltip key={key} content={t(translationPath)}>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              setValue(
                                BulkDriversMessageFormFieldsEnum.message,
                                messageValueWatch + `{{{ ${key} }}} `,
                                { shouldDirty: true, shouldValidate: true },
                              )
                            }
                            onKeyDown={onActivateKeyDown(() =>
                              setValue(
                                BulkDriversMessageFormFieldsEnum.message,
                                messageValueWatch + `{{{ ${key} }}} `,
                                { shouldDirty: true, shouldValidate: true },
                              ),
                            )}
                            className="item pointer"
                          >{`{{{ ${t(translationPath)} }}}`}</span>
                        </FramerTooltip>
                      ) : null,
                  )}
                </PassengersMessageAvailableTemplateStyled>
              </TemplateWrapper>
            </DrawerFormWrapperStyled>
          )}

          {isLoading && (
            <LayoutRightLoader>{t("ui.LOADING_DATA")}...</LayoutRightLoader>
          )}
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
